/**
 * Geld (GELD) Support Registry
 *
 * Support content (hints, explanations, visuals) for GELD families.
 *
 * Module: GELD (Geld)
 * Families: 15 (registry) = 15 (taxonomy)
 * Coverage: 100%
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry';

/**
 * GELD Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const GELD_SUPPORT: Record<string, FamilySupportEntry> = {
  'GELD-TELLEN': {
    familyId: 'GELD-TELLEN',
    defaultExplanation: 'Geld tellen: herken munten en biljetten en tel het totaalbedrag op. Begin met de grootste waarde.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Munten herkennen',
      content: 'Munten: 1c, 2c, 5c, 10c, 20c, 50c, €1, €2\nBiljetten: €5, €10, €20, €50, €100\n\nTel altijd van groot naar klein!'
    }, {
      title: 'Strategie',
      content: 'Stap 1: Sorteer van groot naar klein\nStap 2: Tel de biljetten eerst\nStap 3: Tel de munten erbij\nStap 4: Controleer het totaal'
    }]
  },
  'GELD-MAAK': {
    familyId: 'GELD-MAAK',
    defaultExplanation: 'Om een bedrag te maken, begin je met de grootste munten of biljetten. Tel steeds op tot je het juiste bedrag hebt. Controleer altijd of je totaal klopt.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 350,
        label: 'Bedrag maken',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Strategie',
      content: 'Begin met grote munten/biljetten:\n1. Pak eerst €2 of €1 munten\n2. Vul aan met 50 cent\n3. Werk af met kleinere munten (20c, 10c, 5c)\n\nVoorbeeld: €3,75 = €2 + €1 + 50c + 20c + 5c'
    }, {
      title: 'Controleren',
      content: 'Tel je munten op:\n€2 = 200 cent\n€1 = 100 cent\n50c = 50 cent\n\nOptellen tot je het juiste totaal hebt!'
    }]
  },
  'GELD-TERUG': {
    familyId: 'GELD-TERUG',
    defaultExplanation: 'Wisselgeld bereken je door door te tellen vanaf het bedrag dat je betaalt tot wat je geeft. Begin met kleine munten en werk naar boven.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 365,
        label: 'Wisselgeld',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Strategie: Doortellen',
      content: 'Tel door vanaf het bedrag dat je betaalt tot wat je geeft.\n\nVoorbeeld: €6,35 betalen met €10,00\n€6,35 → €6,40 (+5ct) → €6,50 (+10ct) → €7,00 (+50ct) → €10,00 (+€3)\nWisselgeld: €3,65'
    }]
  },
  'GELD-WISSEL': {
    familyId: 'GELD-WISSEL',
    defaultExplanation: 'Wisselgeld bereken je door het verschil te nemen tussen wat je betaalt en wat het kost.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 135,
        label: 'Wisselgeld',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Methode 1: Aftrekken',
      content: 'Trek de prijs af van wat je betaalt:\n€5,00 - €3,65 = €1,35 wisselgeld'
    }, {
      title: 'Methode 2: Doortellen',
      content: 'Tel door vanaf de prijs:\n€3,65 → €3,70 (+5c) → €4,00 (+30c) → €5,00 (+€1)\nWisselgeld: 5c + 30c + €1 = €1,35'
    }]
  },
  'GELD-KOMMA': {
    familyId: 'GELD-KOMMA',
    defaultExplanation: "Bij euro/cent notatie: het getal voor de komma zijn euro's, na de komma centen. €3,50 = 3 euro en 50 cent = 350 cent.",
    defaultVisual: {
      type: 'money',
      data: {
        amount: 350,
        label: 'Bedrag',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      content: 'Meerdere producten? Tel de prijzen op.\nMeerdere stuks? Vermenigvuldig prijs × aantal.\n\nVoorbeeld: 4 appels van €0,50 = 4 × €0,50 = €2,00'
    }]
  },
  'GELD-WISSELEN': {
    familyId: 'GELD-WISSELEN',
    defaultExplanation: 'Bij valuta wisselen reken je met de wisselkoers. Vermenigvuldig of deel afhankelijk van de richting.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Wisselkoers',
      content: 'Euro naar andere valuta: vermenigvuldig\n€100 × 1,10 (koers) = $110\n\nAndere valuta naar euro: deel\n$110 ÷ 1,10 = €100'
    }]
  },
  'GELD-KOOPJES': {
    familyId: 'GELD-KOOPJES',
    defaultExplanation: 'Om prijzen te vergelijken, bereken je de prijs per stuk of per kilo.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Prijs per stuk',
      content: 'Deel de totaalprijs door het aantal stuks:\n\n3 appels voor €1,50 → €1,50 ÷ 3 = €0,50 per appel\n5 appels voor €2,00 → €2,00 ÷ 5 = €0,40 per appel\n\nGoedkoopst: 5 appels voor €2,00'
    }, {
      title: 'Vergelijken',
      content: 'Bereken altijd de prijs per eenheid (per stuk, per kg, per liter).\nDe laagste prijs per eenheid is de beste deal!'
    }]
  },
  'GELD-BUDGET': {
    familyId: 'GELD-BUDGET',
    defaultExplanation: 'Bij budgetteren tel je alle uitgaven op en trek je die af van je budget.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Tel alle uitgaven op\nStap 2: Trek het totaal af van je budget\nStap 3: Wat overblijft is je restant\n\nVoorbeeld: Budget €20\nUitgaven: €7,50 + €4,25 = €11,75\nOver: €20 - €11,75 = €8,25'
    }, {
      title: 'Controleren',
      content: 'Check altijd:\n- Heb ik genoeg geld?\n- Blijft er iets over?\n- Kan ik dit nog kopen?\n\nAls uitgaven > budget → niet genoeg geld!'
    }]
  },
  'GELD-KORTING': {
    familyId: 'GELD-KORTING',
    defaultExplanation: 'Korting berekenen: bereken het kortingsbedrag en trek het af van de originele prijs.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Bereken het kortingsbedrag\n25% korting op €40 → 25% × €40 = €10\nStap 2: Trek af van de prijs\n€40 - €10 = €30\n\nOf direct: 75% × €40 = €30'
    }, {
      title: 'Handige percentages',
      content: '50% = helft (÷2)\n25% = kwart (÷4)\n10% = tiende (÷10)\n20% = 2 × 10%\n5% = helft van 10%'
    }]
  },
  'GELD-BTW': {
    familyId: 'GELD-BTW',
    defaultExplanation: 'BTW (belasting) wordt bij de prijs opgeteld. In Nederland is het standaardtarief 21%. Prijs inclusief = prijs exclusief × 1,21.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'BTW berekenen',
      content: 'Exclusief → inclusief: × 1,21\n€100 × 1,21 = €121\n\nInclusief → exclusief: ÷ 1,21\n€121 ÷ 1,21 = €100'
    }, {
      title: 'BTW-tarieven',
      content: 'Hoog tarief: 21% (de meeste producten)\nLaag tarief: 9% (voedsel, boeken, medicijnen)\n\nLaag: × 1,09 of ÷ 1,09'
    }]
  },
  'GELD-TERUGREKENEN': {
    familyId: 'GELD-TERUGREKENEN',
    defaultExplanation: 'Bij terugrekenen ken je de nieuwe prijs en het percentage, en zoek je de originele prijs.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Na 20% korting kost iets €80.\nDan is €80 = 80% van de originele prijs.\nOrigineel = €80 ÷ 0,80 = €100'
    }]
  },
  'GELD-SPAREN': {
    familyId: 'GELD-SPAREN',
    defaultExplanation: 'Bij sparen groeit je geld door rente. Rente = spaarbedrag × rentepercentage.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Rente berekenen',
      content: 'Rente = bedrag × percentage\n€1.000 × 2% = €1.000 × 0,02 = €20 rente\n\nNa 1 jaar: €1.000 + €20 = €1.020'
    }]
  },
  'GELD-RENTE-PROC': {
    familyId: 'GELD-RENTE-PROC',
    defaultExplanation: 'Bij samengestelde rente krijg je ook rente over je rente. Na meerdere jaren groeit het bedrag sneller.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Samengestelde rente',
      content: 'Jaar 1: €1.000 × 1,05 = €1.050\nJaar 2: €1.050 × 1,05 = €1.102,50\n\nFormule: eindbedrag = startbedrag × (1 + rente)^jaren'
    }]
  },
  'GELD-BOODSCHAPPEN': {
    familyId: 'GELD-BOODSCHAPPEN',
    defaultExplanation: 'Bij boodschappen tel je alle prijzen op. Let op: meerdere stuks = prijs × aantal.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Noteer alle producten en prijzen\nStap 2: Vermenigvuldig prijs × aantal per product\nStap 3: Tel alle bedragen op\nStap 4: Controleer of je genoeg geld hebt'
    }]
  },
  'GELD-PRIJS-PER': {
    familyId: 'GELD-PRIJS-PER',
    defaultExplanation: 'Prijs per eenheid bereken je door de totaalprijs te delen door het aantal/gewicht. Zo kun je producten eerlijk vergelijken.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Berekenen',
      content: 'Prijs per kg = totaalprijs ÷ aantal kg\n€6 voor 2 kg → €6 ÷ 2 = €3 per kg\n\nPrijs per stuk = totaalprijs ÷ aantal stuks\n€4,50 voor 3 stuks → €1,50 per stuk'
    }]
  }
};

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const GELD_SUPPORT_STATS = {
  moduleCode: 'GELD',
  moduleName: 'Geld',
  totalFamilies: Object.keys(GELD_SUPPORT).length,
  taxonomyFamilies: 15,
  orphanFamilies: 0,
  withHints: Object.values(GELD_SUPPORT).filter(e => e.hints?.length).length,
  withVisuals: Object.values(GELD_SUPPORT).filter(e => e.defaultVisual).length,
  withExplanations: Object.values(GELD_SUPPORT).filter(e => e.defaultExplanation).length,
  requiresHintsCount: Object.values(GELD_SUPPORT).filter(e => e.requiresHints).length,
  requiresVisualCount: Object.values(GELD_SUPPORT).filter(e => e.requiresVisual).length
};